import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Loader2,
    Wand2,
    Volume2,
    Turtle,
    Plus,
    ArrowLeft,
    AlertTriangle,
    MapPin,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { handlePaywallError, formatQuotaLine, isQuotaExhausted } from "@/lib/access";
import { useQuotas } from "@/hooks/use-quotas";
import { ttsLanguageOf, voiceOf } from "@/lib/lang";
import { useDashboard } from "@/contexts/DashboardContext";

function TTSButton({ text, language, testId }) {
    const { data: dash } = useDashboard();
    const voice = voiceOf(dash?.profile);
    const [busy, setBusy] = useState(false);
    async function play(speed) {
        if (!text) return;
        setBusy(true);
        try {
            const { data } = await api.post("/poc/tts", {
                text,
                language: ttsLanguageOf(language),
                speed,
                voice,
            });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await audio.play();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }
    return (
        <div className="inline-flex gap-1.5">
            <button
                onClick={() => play("natural")}
                disabled={busy}
                data-testid={`${testId}-natural`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-ink text-white hover:bg-emerald disabled:opacity-60"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />}
            </button>
            <button
                onClick={() => play("slow")}
                disabled={busy}
                data-testid={`${testId}-slow`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-ink text-white hover:bg-emerald disabled:opacity-60"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Turtle className="w-3 h-3" />}
            </button>
        </div>
    );
}

const CONTEXT_SUGGESTIONS = [
    "email professionnel",
    "conversation entre amis",
    "réunion formelle",
    "message SMS",
    "présentation orale",
    "commande au restaurant",
];

const VARIANT_STYLES = {
    littérale: {
        badge: "bg-paperAlt text-inkSoft border-lexiBorder",
    },
    naturelle: {
        badge: "bg-emerald text-white border-emerald",
        highlighted: true,
    },
    formelle: {
        badge: "bg-ink text-white border-ink",
    },
    familière: {
        badge: "bg-terracotta text-white border-terracotta",
    },
};

export default function Translate() {
    const [text, setText] = useState("");
    const [context, setContext] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [added, setAdded] = useState({});
    const { refresh: refreshDashboard } = useDashboard();
    const { data: quotas, refetch: refetchQuotas } = useQuotas();
    const navigate = useNavigate();
    const translateFeature = quotas?.features?.translate;
    const quotaLine = formatQuotaLine(translateFeature);
    const quotaExhausted = isQuotaExhausted(translateFeature);

    async function translate() {
        if (!text.trim()) return;
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/translate", {
                text: text.trim(),
                context: context.trim() || null,
            });
            setResult(data);
            refetchQuotas();
        } catch (err) {
            if (handlePaywallError(err)) {
                refetchQuotas();
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setLoading(false);
        }
    }

    async function addVariant(variant) {
        if (!variant?.text || !result) return;
        setAdded((s) => ({ ...s, [variant.type]: "pending" }));
        try {
            const { data } = await api.post("/translate/add_to_reviews", {
                word: variant.text,
                translation_fr: text.trim(),
                natural_sentence: variant.text,
                sentence_translation: text.trim(),
            });
            setAdded((s) => ({ ...s, [variant.type]: data.added ? "added" : "already" }));
            toast.success(data.added ? "Ajouté à vos révisions." : "Déjà dans vos révisions.");
            if (data.added) refreshDashboard?.();
        } catch (err) {
            toast.error(formatApiError(err));
            setAdded((s) => ({ ...s, [variant.type]: null }));
        }
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="translate-page">
            <button
                onClick={() => navigate("/dashboard")}
                className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                data-testid="translate-back"
            >
                <ArrowLeft className="w-4 h-4" /> Tableau de bord
            </button>

            <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Traducteur contextuel</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="translate-title">
                Quatre traductions. Un seul contexte. Le bon registre.
            </h1>
            <p className="mt-3 text-sm text-inkSoft max-w-2xl">
                Entrez un texte à traduire, précisez si besoin le contexte. L&apos;IA renvoie une version littérale,
                naturelle, formelle et familière — avec le registre recommandé pour votre situation.
            </p>

            <div className="mt-8 bg-white border border-lexiBorder rounded-2xl p-5 md:p-6" data-testid="translate-input-card">
                <label className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">Texte à traduire</label>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) translate();
                    }}
                    placeholder="Ex : bonjour, comment allez-vous ?"
                    data-testid="translate-text"
                    className="mt-2 w-full min-h-[80px] rounded-xl border border-lexiBorder bg-paper p-3 focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
                <label className="mt-4 block text-[11px] tracking-[0.22em] uppercase text-inkSoft">
                    Contexte <span className="normal-case text-inkSoft">(optionnel)</span>
                </label>
                <input
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Ex : email professionnel, conversation entre amis…"
                    data-testid="translate-context"
                    className="mt-2 w-full rounded-xl border border-lexiBorder bg-paper p-3 focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                    {CONTEXT_SUGGESTIONS.map((s) => (
                        <button
                            key={s}
                            onClick={() => setContext(s)}
                            data-testid={`translate-context-suggest-${s.replace(/\s+/g, "-")}`}
                            className="text-[11px] px-2.5 py-1 rounded-full bg-paperAlt border border-lexiBorder hover:bg-paper text-inkSoft"
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <div className="mt-5 flex justify-end items-center gap-3">
                    {quotaLine && (
                        <span
                            className={`text-[11px] rounded-full px-3 py-1 border ${
                                quotaExhausted
                                    ? "bg-terracotta/10 text-terracotta border-terracotta/40 font-semibold"
                                    : "text-inkSoft bg-paperAlt border-lexiBorder"
                            }`}
                            data-testid="translate-quota"
                        >
                            {quotaLine}
                        </span>
                    )}
                    <button
                        onClick={translate}
                        disabled={!text.trim() || loading}
                        data-testid="translate-submit"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        Traduire
                    </button>
                </div>
            </div>

            {result && (
                <div className="mt-8 space-y-4" data-testid="translate-result">
                    <div className="text-sm text-inkSoft">
                        <span className="font-medium text-ink">{result.source_language_fr}</span> →{" "}
                        <span className="font-medium text-ink">{result.target_language_fr}</span>
                        {result.detected_context_fr ? ` · ${result.detected_context_fr}` : ""}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(result.variants || []).map((v, i) => {
                            const style = VARIANT_STYLES[v.type] || VARIANT_STYLES.naturelle;
                            const isRecommended = v.type === result.recommended_variant;
                            return (
                                <article
                                    key={i}
                                    data-testid={`variant-${v.type}`}
                                    className={`bg-white rounded-2xl p-5 border transition-all ${
                                        isRecommended
                                            ? "border-emerald shadow-sm ring-1 ring-emerald/20"
                                            : "border-lexiBorder"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span
                                            className={`text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full border font-medium ${style.badge}`}
                                        >
                                            {v.type}
                                            {isRecommended ? " · recommandé" : ""}
                                        </span>
                                        <TTSButton
                                            text={v.text}
                                            language={result.target_language_fr}
                                            testId={`variant-${v.type}-tts`}
                                        />
                                    </div>
                                    <p className="mt-3 font-serif text-xl leading-snug">« {v.text} »</p>
                                    {v.when_to_use_fr && (
                                        <p className="mt-2 text-xs text-inkSoft italic">{v.when_to_use_fr}</p>
                                    )}
                                    <div className="mt-3 flex items-center justify-between">
                                        <span className="text-[11px] text-inkSoft">{v.register_fr}</span>
                                        <button
                                            onClick={() => addVariant(v)}
                                            disabled={added[v.type] === "added" || added[v.type] === "pending"}
                                            data-testid={`variant-${v.type}-add`}
                                            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-terracotta hover:bg-terracottaDark text-white disabled:opacity-60"
                                        >
                                            {added[v.type] === "pending" ? (
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                                <Plus className="w-3 h-3" />
                                            )}
                                            {added[v.type] === "added" ? "Ajouté" : "Réviser"}
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                    {(result.false_friends || []).length > 0 && (
                        <div className="bg-terracotta/8 border border-terracotta/30 rounded-2xl p-5" data-testid="translate-traps">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 text-terracotta" />
                                <p className="text-[10px] tracking-[0.22em] uppercase text-terracotta font-semibold">
                                    Pièges à connaître
                                </p>
                            </div>
                            <ul className="text-sm space-y-1">
                                {result.false_friends.map((f, i) => (
                                    <li key={i}>
                                        <span className="text-ink">« {f.trap} »</span> —{" "}
                                        <span className="text-inkSoft">{f.explanation_fr}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {(result.regional_variants || []).length > 0 && (
                        <div className="bg-white border border-lexiBorder rounded-2xl p-5" data-testid="translate-regional">
                            <div className="flex items-center gap-2 mb-2">
                                <MapPin className="w-4 h-4 text-emerald" />
                                <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                                    Variantes régionales
                                </p>
                            </div>
                            <ul className="text-sm space-y-1">
                                {result.regional_variants.map((r, i) => (
                                    <li key={i}>
                                        <span className="text-inkSoft mr-2 text-xs">{r.region_fr} :</span>
                                        <span className="font-serif italic">« {r.text} »</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {result.notes_fr && (
                        <p className="text-xs text-inkSoft italic" data-testid="translate-notes">
                            {result.notes_fr}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
